import {Component, OnInit} from '@angular/core';
import {GeneralService} from "../../../../services/generalService";
import {NotificationService} from "../../../../services/notificationService";
import {DICTIONARY} from "../../../../dictionary";
import _ from 'lodash';
import {RouteService} from "../../../../services/routeService";
import {CollaborationProtectionService} from "../../../../services/collaborationProtectionService";

@Component({
	selector: 'app-collaboration-threat-events',
	templateUrl: './collaboration-threat-events.component.html',
	styleUrls: ['./collaboration-threat-events.component.scss'],
})
export class CollaborationThreatEventsComponent implements OnInit {
	constructor(public gs:GeneralService,
				private rs:RouteService,
				private cps:CollaborationProtectionService,
				private ns:NotificationService) {}

	_ = _;
	dic = DICTIONARY;

	viewTypes = {timeline: 'timeline', list: 'list'};
	selectedViewType = this.viewTypes.timeline;
	loadingInProcess = false;

	filterData;
	eventsByDates: any[] = [];
	flatEventsByDate;
	eventDetailsPopup;
	previousEventDetailsPopup;
	currentAssetInfoObj;
	actionInProcess = false;

	reviewerActionsDic = {
		resolved: {display: 'Updated status to "Resolved"', color: 'success', icon: 'far fa-check-circle'},
		safe: {display: 'Updated status to "Safe"', color: 'success', icon: 'far fa-check-circle'},
		pending: {display: 'Updated status to "Pending"', color: 'warning', icon: 'far fa-clock'},
		note: {display: 'note', color: 'success', icon: 'far fa-info'},
		quarantine: {display: 'File was quarantined', color: 'danger', icon: 'far fa-lock'},
		release: {display: 'File was released from quarantine', color: 'warning', icon: 'far fa-unlock'},
	};

	AGGREGATION_GAP_MS = 30 * 60 * 1000; // 30 minutes
	SEVERITY_ORDER = {critical: 0, high: 1, medium: 2, low: 3};

	assets;
	assetsByResource: Record<string, any[]> = {};
	rawEvents: any[] = []; // original flat events before aggregation
	events: any[] = []; // mixed: aggregated groups + standalone events
	eventsList;
	usersInEventsList;
	viewAllUsers = true;
	searchUserTxt;
	selectedSeverities: string[] = [];

	linkScopeTooltips: Record<string, string> = {
		anonymous: 'Anyone with the link can access (no login required)',
		organization: 'All users in your company can access (must sign in)',
		users: 'Only users with existing access (must sign in)',
		external: 'At least one or more external user has an access to this file'
	};

	ngOnInit() {
		this.initFilters();
		Promise.all([this.getCollaborationEvents(), this.getCollaborationAssets()]).then(() => {
			this.rawEvents.forEach(event => {
				event.assets = this.assetsByResource[event.resource_id?._id] || [];
			});
		});
	}

	getCollaborationEvents = () => {
		this.loadingInProcess = true;
		return this.rs.getCollaborationThreatEvents().then((response) => {
			response.forEach((eventObj) => {
				this.cps.parseResourceName(eventObj.resource_id);
			});

			this.rawEvents = (response || []).map(event => ({
				...event,
				time: new Date(event.created),
				type: this.mapEventType(event.event_type),
				description: this.eventTypeTooltip(event.event_type),
				severity: event.severity || 'medium',
				status: event.status || 'pending',
				user: event.user || '',
				resource_type: event.resource_id?.resource_type || 'Unknown',
				file: event.file_name || null,
				url: event.malicious_info?.url,
				confidence: event.details?.confidence || event.details?.sensitivity_score || 0,
				resourceName: event.resource_id?.name,
			}));

			this.events = this.aggregateEvents(this.rawEvents);
			this.eventsList = [...this.events];
			this.prepareTimeLine();
			this.loadingInProcess = false;
		}, (err) => {
			this.loadingInProcess = false;
		});
	}

	getCollaborationAssets = () => {
		return this.rs.getCollaborationAssets().then((response) => {
			this.assets = response;
			this.assets.forEach((assetObj) => {
				this.cps.parseResourceName(assetObj.resource_id);
				this.parseAssetDetails(assetObj);
			});

			this.assetsByResource = _.groupBy(this.assets, (assetObj) => assetObj.resource_id?._id);
		}, (err) => {
		});
	}

	parseAssetDetails = (assetObj) => {
		if (assetObj.sensitivity_info) {
			assetObj._topSensitiveTypes = (assetObj.sensitivity_info.sensitive_types || [])
				.filter(t => t.score)
				.sort((a, b) => (b.score || 0) - (a.score || 0))
				.slice(0, 3)
				.map(t => t.sensitive_type);
		}

		if (assetObj.malicious_info) {
			assetObj._maliciousSubCategory = (assetObj.malicious_info.sub_status || [])
				.map(s => s.message)
				.slice(0, 2);
		}
	}

	eventTypeDic = DICTIONARY.CONSTANTS.collaborationThreatEventTypes;

	mapEventType(eventType: string): string {
		return this.eventTypeDic[eventType]?.name || eventType;
	}

	eventTypeTooltip(eventType: string): string {
		return this.eventTypeDic[eventType]?.tooltip || '';
	}

	// --- Smart Event Aggregation ---

	aggregateEvents(events: any[]): any[] {
		if (!events?.length) return [];

		// Group by event_type + user
		const buckets = _.groupBy(events, e => `${e.event_type}|${e.user}`);
		const result = [];

		for (const key of Object.keys(buckets)) {
			const bucket = _.sortBy(buckets[key], e => e.time.getTime());
			let currentGroup = [bucket[0]];

			for (let i = 1; i < bucket.length; i++) {
				const gap = bucket[i].time.getTime() - currentGroup[currentGroup.length - 1].time.getTime();
				if (gap <= this.AGGREGATION_GAP_MS) {
					currentGroup.push(bucket[i]);
				} else {
					this.flushGroup(currentGroup, result);
					currentGroup = [bucket[i]];
				}
			}
			this.flushGroup(currentGroup, result);
		}

		return _.orderBy(result, e => e.time.getTime(), 'desc');
	}

	private flushGroup(group: any[], result: any[]) {
		if (group.length < 2) {
			result.push(group[0]);
		} else {
			result.push(this.createAggregatedEvent(group));
		}
	}

	private createAggregatedEvent(children: any[]) {
		const sorted = _.sortBy(children, e => e.time.getTime());
		const resourceNames = _.uniq(children.map(e => {
			return e.resourceName ? e.resourceName + (e.resource_type === 'OneDrive' ? ` (${e.resource_type})` : '') : null;
		}).filter(Boolean)).slice(0, 3);

		return {
			_isAggregated: true,
			_aggregationKey: `${children[0].event_type}|${children[0].user}`,
			event_type: children[0].event_type,
			type: children[0].type,
			description: children[0].description,
			user: children[0].user,
			severity: this.getHighestSeverity(children),
			status: this.getWorstStatus(children),
			children,
			count: children.length,
			time: sorted[sorted.length - 1].time,
			timeRange: {start: sorted[0].time, end: sorted[sorted.length - 1].time},
			resourceNames,
			totalFiles: _.uniq(children.map(e => e.file).filter(Boolean)).length,
			resource_type: children[0].resource_type,
			isExpanded: false,
		};
	}

	getHighestSeverity(events: any[]): string {
		return events.reduce((best, e) => (this.SEVERITY_ORDER[e.severity] ?? 99) < (this.SEVERITY_ORDER[best] ?? 99) ? e.severity : best, events[0].severity);
	}

	getWorstStatus(events: any[]): string {
		const statusOrder = {pending: 0, safe: 1, resolved: 2};
		return events.reduce((worst, e) => (statusOrder[e.status] ?? 99) < (statusOrder[worst] ?? 99) ? e.status : worst, events[0].status);
	}

	refreshAggregatedGroups() {
		this.events.forEach(event => {
			if (!event._isAggregated) return;
			event.severity = this.getHighestSeverity(event.children);
			event.status = this.getWorstStatus(event.children);
			event.count = event.children.length;
		});
	}

	toggleGroup(group: any) {
		group.isExpanded = !group.isExpanded;
		this.flattenEventsByDateList();
	}

	toggleExpandItem = (item, event, table?) => {
		if (!item.children?.length) {
			return;
		}
		item.isExpanded = !item.isExpanded;
		event.stopPropagation();
		if (table) {
			table.searchItem();
		}
	}

	hasChildrenPending(group: any): boolean {
		return group.children?.some(c => c.status === 'pending');
	}

	changeViewType(viewType) {
		this.selectedViewType = viewType;
		if (viewType === this.viewTypes.timeline) {
			this.prepareTimeLine();
		} else {
			this.initFilters();
			this.searchEvents('', null);
		}
	}

	prepareTimeLine() {
		const previousSelections = this.usersInEventsList ? _.keyBy(this.usersInEventsList, 'email') : {};
		const usersMap = {};
		const dateGroups = {};

		this.events.forEach(event => {
			const email = event.user;
			const isNewUser = !usersMap[email];

			if (isNewUser) {
				usersMap[email] = {
					displayName: email || 'Unknown',
					email,
					selected: this.viewAllUsers || !!previousSelections[email]?.selected
				};
			}

			const isEventShouldShow = (this.viewAllUsers || usersMap[email].selected) && (!this.selectedSeverities.length || this.selectedSeverities.includes(event.severity));
			if (isEventShouldShow) {
				const dateKey = new Date(event.time).toDateString();
				if (!dateGroups[dateKey]) {
					dateGroups[dateKey] = {
						date: new Date(dateKey),
						events: []
					};
				}
				dateGroups[dateKey].events.push(event);
			}
		});

		this.usersInEventsList = Object.values(usersMap);
		this.eventsByDates = Object.values(dateGroups);

		this.flattenEventsByDateList();
	}

	flattenEventsByDateList = () => {
		this.flatEventsByDate = [];
		for (const dateGroup of this.eventsByDates) {
			this.flatEventsByDate.push({viewType: 'date', date: dateGroup.date});
			const items = dateGroup.events;
			for (let i = 0; i < items.length; i++) {
				const event = items[i];
				const isLastBase = i === items.length - 1;

				if (event._isAggregated) {
					event.viewType = 'group';
					event.isLastInDate = isLastBase && !event.isExpanded;
					this.flatEventsByDate.push(event);

					if (event.isExpanded) {
						for (let j = 0; j < event.children.length; j++) {
							const child = event.children[j];
							child.viewType = 'groupChild';
							child.isLastInDate = isLastBase && j === event.children.length - 1;
							this.flatEventsByDate.push(child);
						}
					}
				} else {
					event.viewType = 'event';
					event.isLastInDate = isLastBase;
					this.flatEventsByDate.push(event);
				}
			}
		}
	}

	selectViewUser = (user) => {
		switch (user) {
			case 'all':
				const selectAll = !this.usersInEventsList.every(u => u.selected);
				this.usersInEventsList.forEach(u => u.selected = selectAll);
				this.viewAllUsers = selectAll;

				if (!selectAll) {
					this.usersInEventsList[0].selected = true;
					this.viewAllUsers = false;
				}
				break;

			default:
				user.selected = !user.selected;

				if (!this.usersInEventsList.some(u => u.selected)) {
					this.usersInEventsList[0].selected = true;
				}

				this.viewAllUsers = this.usersInEventsList.every(u => u.selected);
				break;
		}

		this.prepareTimeLine();
	}

	selectViewSeverity = (severity: string) => {
		const idx = this.selectedSeverities.indexOf(severity);
		if (idx > -1) {
			this.selectedSeverities.splice(idx, 1);
		} else {
			this.selectedSeverities.push(severity);
		}
		this.prepareTimeLine();
	}

	searchUser = (searchTerm) => {
		searchTerm = (searchTerm || '').toLowerCase();
		this.usersInEventsList.forEach(user => {
			user.hide = !user.email?.toLowerCase().includes(searchTerm);
		});
	}

	clearSearchUser = () => {
		this.searchUserTxt = '';
		this.usersInEventsList.forEach(user => {
			user.hide = false;
		});
	}

	colorForEvent(severity: string) {
		switch (severity) {
			case 'critical': return 'darkred';
			case 'high': return '#e85d04';
			case 'medium': return '#f59e0b';
			case 'low': return '#16a34a';
			default: return '#64748b';
		}
	}

	initFilters = () => {
		this.filterData = {
			filterType: 'collaborationThreatEvents',
			filters: {
				severity: ['critical', 'high', 'medium', 'low'],
				status: ['pending', 'resolved', 'safe'],
				type: Object.values(this.eventTypeDic).map((t: any) => t.name),
			},
			initFiltersFunction: this.initFilters,
		};
	}

	searchEvents = (searchTerm, activeFilters) => {
		this.eventsList.forEach(record => {
			const itemsToSearch = record._isAggregated ? record.children : [record];
			const anyMatch = itemsToSearch.some(item => {
				if (searchTerm) {
					const lc = searchTerm.toLowerCase();
					const isFound = item.user?.toLowerCase().includes(lc) ||
						item.type?.toLowerCase().includes(lc) ||
						item.source?.toLowerCase().includes(lc) ||
						(item.file || '').toLowerCase().includes(lc);
					if (!isFound) return false;
				}

				if (activeFilters) {
					let numFilterToMatch = Object.keys(activeFilters).length;
					if (activeFilters['severity']?.length) {
						if (activeFilters['severity'].includes(item.severity)) numFilterToMatch--;
					}
					if (activeFilters['status']?.length) {
						if (activeFilters['status'].includes(item.status)) numFilterToMatch--;
					}
					if (activeFilters['type']?.length) {
						if (activeFilters['type'].includes(item.type)) numFilterToMatch--;
					}
					if (numFilterToMatch) return false;
				}

				return true;
			});
			record.hide = !anyMatch;
		});
	}

	showEventActions = (eventObj) => {
		if (eventObj._isAggregated) {
			const actions = ['View Details'];
			if (eventObj.children.some(c => c.status === 'pending')) {
				actions.push('Mark All Resolved');
			}
			actions.push('Delete All');
			return actions;
		}
		const actions = ['View Details'];
		if (eventObj.status === 'pending') {
			actions.push('Mark Resolved');
			actions.push('Allow & Ignore');
		}
		actions.push('Delete');
		return actions;
	}

	selectAction = (eventObj, action) => {
		if (!eventObj) return;
		switch (action) {
			case 'View Details':
				this.openEvent(eventObj);
				break;

			case 'Mark Resolved':
				this.markResolved([eventObj]);
				break;

			case 'Mark All Resolved':
				this.markResolved(eventObj.children);
				break;

			case 'Allow & Ignore':
				this.markSafe(eventObj);
				break;

			case 'Delete':
				this.deleteEvents([eventObj]);
				break;

			case 'Delete All':
				this.deleteEvents(eventObj.children);
				break;
		}
	}

	showMultipleActions = () => {
		return [
			'Mark Resolved',
			'Delete',
		];
	};


	selectMultipleAction = (selectedItems, action) => {
		switch (action) {
			case 'Delete':
				this.deleteEvents(selectedItems);
				break;

			case 'Mark Resolved':
				this.markResolved(selectedItems);
				break;

		}
	};

	deleteEvents = (events) => {
		this.gs.showPopup({
			title: `Delete Event`,
			subTitle: events.length > 1 ? `You are about to delete ${events.length} events` : `You are about to delete event ${events[0].name || ''}`,
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Delete',
			doneCb: () => {
				const deletePromises = events.map((eventObj) =>
					this.rs.deleteCollaborationThreatEvents(eventObj._id).then(() => {
						this.rawEvents = this.rawEvents.filter(m => m._id !== eventObj._id);
					}, (err) => {})
				);
				Promise.all(deletePromises).then(() => {
					this.events = this.aggregateEvents(this.rawEvents);
					this.eventsList = [...this.events];
					this.prepareTimeLine();
					this.ns.showInfoMessage(this.dic.MESSAGES.operationsSuccess);
				});
			}
		});
	}

	exportToCsv = (sortBy?) => {
		if (!this.rawEvents?.length) {
			this.ns.showWarnMessage(this.dic.ERRORS.noDataToExportCsv);
			return;
		}
		let csvString = 'Time,User,Source,Type,Severity,Status,File,Detail\n';
		let rows = [...this.rawEvents];
		if (sortBy) {
			rows = this.gs.sortTable(rows, sortBy);
		}
		rows.forEach(e => {
			csvString += `"${e.time.toISOString()}","${e.user}","${e.source}","${e.type}","${e.severity}","${e.status}","${e.file || ''}","${(e.detail || '').replace(/"/g, '""')}"\n`;
		});
		this.gs.exportCsv(csvString, 'threat_events.csv');
	}

	openEvent(eventObj: any) {
		if (this.eventDetailsPopup?.show) {
			this.previousEventDetailsPopup = this.eventDetailsPopup;
		}
		this.eventDetailsPopup = {
			show: true,
			eventObj,
			reviewerNote: ''
		};
	}

	markResolved(events: any) {
		events = events.filter(itm => itm.status !== 'resolved');
		if (!events.length) {
			this.ns.showInfoMessage('Selected event are already resolved');
			return;
		}

		this.gs.showPopup({
			title: `Resolve ${events.length > 1 ? 'Events' : 'Event'}`,
			subTitle: events.length > 1 ? `Are you sure you want to resolve ${events.length} events` : `Are you sure you want to resolve this event?`,
			type: this.dic.CONSTANTS.popupWarning,
			doneBtnText: 'Confirm',
			doneCb: () => {
				events.forEach((eventObj) => {
					const actionInfo = {
						action: 'status',
						status: 'resolved'
					};
					this.rs.updateCollaborationThreatEvent(eventObj._id, actionInfo).then(() => {
						eventObj.status = 'resolved';
						this.refreshAggregatedGroups();
						this.ns.showInfoMessage('Event marked as resolved');
					}, () => {
					});
				});
			}
		});
	}

	markSafe(event: any) {
		this.actionInProcess = true;
		const actionInfo = {
			action: 'status',
			status: 'safe'
		};
		this.rs.updateCollaborationThreatEvent(event._id, actionInfo).then(() => {
			event.status = 'safe';
			this.refreshAggregatedGroups();
			this.actionInProcess = false;
			this.ns.showInfoMessage('Event marked as safe');
		}, () => {
			this.actionInProcess = false;
		});
	}

	severityClass(severity: string) {
		return {
			'sev-critical': severity === 'critical',
			'sev-high': severity === 'high',
			'sev-medium': severity === 'medium',
			'sev-low': severity === 'low',
		};
	}

	statusClass(status: string) {
		return {
			'status-pending': status === 'pending',
			'status-resolved': status === 'resolved',
			'status-fp': status === 'safe',
		};
	}

	statusLabel(status: string) {
		return {pending: 'Pending Review', resolved: 'Resolved', safe: 'Safe'}[status] || status;
	}

	sensitivityScoreColor(score: number) {
		if (score >= 5) return '#FF4136';
		if (score >= 4) return '#FF851B';
		if (score >= 1) return 'darkorange';
		return 'limegreen';
	}

	showAssetActions = (assetObj) => {
		const actions = ['View info'];
		return actions;
	}

	selectAssetAction = (assetObj, action) => {
		switch (action) {
			case 'View info':
				this.viewAssetDetails(assetObj);
				break;

		}
	}

	viewAssetDetails = (assetObj) => {
		this.currentAssetInfoObj = assetObj;
	}
	protected readonly JSON = JSON;
}
